<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import TemplateCard from '../../templates/TemplateCard.vue';
import TemplatePreviewDrawer from '../../templates/TemplatePreviewDrawer.vue';
import {
  formatTemplateDate,
  formatTemplateLanguage,
  groupTemplates,
  templateTypeKey,
} from '../../templates/templateUtils';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    required: true,
  },
});

const TEMPLATE_LEARN_MORE_URL =
  'https://p3dr0.com.br/hc/user-guide/articles/1754940076-whatsapp-templates';

const store = useStore();
const { t } = useI18n();

const templates = ref([]);
const searchQuery = ref('');
const selectedLanguage = ref('all');
const selectedType = ref('all');
const selectedTemplate = ref(null);
const openFilterMenu = ref(null);
const previewPanelRef = ref(null);
const lastSyncAttemptAt = ref(null);
const isLoading = ref(false);
const isSyncing = ref(false);

const hasTemplates = computed(() => templates.value.length > 0);

const inbox = computed(() => store.getters['inboxes/getInbox'](props.inboxId));
// Meta's template creation API is only reachable for native WhatsApp Cloud
// inboxes; 360dialog/Twilio templates are still managed with the provider.
const canCreateTemplate = computed(
  () => inbox.value?.provider === 'whatsapp_cloud'
);

const typeLabels = computed(() => ({
  TEXT: t('WHATSAPP_TEMPLATE_MGMT.TYPES.TEXT'),
  IMAGE: t('WHATSAPP_TEMPLATE_MGMT.TYPES.IMAGE'),
  VIDEO: t('WHATSAPP_TEMPLATE_MGMT.TYPES.VIDEO'),
  DOCUMENT: t('WHATSAPP_TEMPLATE_MGMT.TYPES.DOCUMENT'),
  MEDIA: t('WHATSAPP_TEMPLATE_MGMT.TYPES.MEDIA'),
  QUICK_REPLY: t('WHATSAPP_TEMPLATE_MGMT.TYPES.QUICK_REPLY'),
  CALL_TO_ACTION: t('WHATSAPP_TEMPLATE_MGMT.TYPES.CALL_TO_ACTION'),
  CATALOG: t('WHATSAPP_TEMPLATE_MGMT.TYPES.CATALOG'),
  COPY_CODE: t('WHATSAPP_TEMPLATE_MGMT.TYPES.COPY_CODE'),
}));

const languageOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_LANGUAGES'),
  },
  ...[...new Set(templates.value.map(template => template.language))]
    .filter(Boolean)
    .sort()
    .map(language => ({
      value: language,
      label: formatTemplateLanguage(language),
    })),
]);

const typeOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_TYPES'),
  },
  ...[...new Set(templates.value.map(templateTypeKey))]
    .map(type => ({
      value: type,
      label: typeLabels.value[type],
    }))
    .sort((first, second) => first.label.localeCompare(second.label)),
]);

const filterMenus = computed(() =>
  [
    {
      key: 'language',
      icon: 'i-lucide-languages',
      options: languageOptions.value,
      active: selectedLanguage.value,
    },
    {
      key: 'type',
      icon: 'i-lucide-layout-template',
      options: typeOptions.value,
      active: selectedType.value,
    },
  ].map(menu => {
    const items = menu.options.map(option => ({
      ...option,
      action: menu.key,
      isSelected: option.value === menu.active,
    }));

    return {
      ...menu,
      items,
      selected: items.find(item => item.isSelected) || items[0],
    };
  })
);

const closeFilterMenu = () => {
  openFilterMenu.value = null;
};

const toggleFilterMenu = key => {
  openFilterMenu.value = openFilterMenu.value === key ? null : key;
};

const handleFilterAction = ({ action, value }) => {
  closeFilterMenu();
  if (action === 'language') selectedLanguage.value = value;
  else selectedType.value = value;
};

const openPreview = template => {
  selectedTemplate.value = template;
  previewPanelRef.value?.open();
};

const filteredTemplates = computed(() => {
  let records = templates.value;

  if (selectedLanguage.value !== 'all') {
    records = records.filter(
      template => template.language === selectedLanguage.value
    );
  }

  if (selectedType.value !== 'all') {
    records = records.filter(
      template => templateTypeKey(template) === selectedType.value
    );
  }

  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return records;

  return records.filter(template =>
    [template.name, template.searchableContent].some(value =>
      value?.toLowerCase().includes(query)
    )
  );
});

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getMessageTemplates(props.inboxId);

    if (!Array.isArray(data.payload)) throw new TypeError();

    lastSyncAttemptAt.value = data.meta?.last_sync_attempt_at || null;
    templates.value = groupTemplates(
      data.payload.map(template => ({
        template,
        inbox: inbox.value,
        lastUpdatedAt: data.meta?.last_sync_attempt_at,
      }))
    );

    if (
      !languageOptions.value.some(
        ({ value }) => value === selectedLanguage.value
      )
    )
      selectedLanguage.value = 'all';
    if (!typeOptions.value.some(({ value }) => value === selectedType.value))
      selectedType.value = 'all';
  } catch {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const syncTemplates = async () => {
  if (isSyncing.value) return;

  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.SYNC_SUCCESS'));
    await fetchTemplates();
  } catch {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="gap-4 mx-6">
    <div
      class="px-5 py-5 space-y-5 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-heading-3 text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATE_MGMT.TITLE') }}
          </span>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATE_MGMT.DESCRIPTION') }}
            <a
              :href="TEMPLATE_LEARN_MORE_URL"
              class="text-sm font-medium text-n-blue-11 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ t('WHATSAPP_TEMPLATE_MGMT.KNOW_MORE') }}
            </a>
          </p>
          <span
            v-if="lastSyncAttemptAt"
            class="block mt-1 text-xs text-n-slate-10"
          >
            {{
              t('WHATSAPP_TEMPLATE_MGMT.LAST_SYNC_ATTEMPT', {
                date: formatTemplateDate(lastSyncAttemptAt),
              })
            }}
          </span>
        </div>
        <div class="flex flex-shrink-0 gap-2 items-center">
          <router-link
            :to="{ name: 'settings_templates', query: { inbox_id: inboxId } }"
            class="text-sm font-medium text-n-blue-11 hover:underline"
          >
            {{ t('WHATSAPP_TEMPLATE_MGMT.MANAGE_ALL') }}
          </router-link>
          <Button
            :label="t('WHATSAPP_TEMPLATE_MGMT.SYNC_TEMPLATES')"
            icon="i-lucide-refresh-cw"
            color="slate"
            size="sm"
            :is-loading="isSyncing"
            :disabled="isSyncing"
            @click="syncTemplates"
          />
          <router-link
            v-if="canCreateTemplate"
            :to="{ name: 'settings_inbox_new_template', params: { inboxId } }"
          >
            <Button
              :label="t('WHATSAPP_TEMPLATE_MGMT.CREATE_TEMPLATE')"
              icon="i-lucide-plus"
              size="sm"
            />
          </router-link>
        </div>
      </div>

      <div
        v-if="hasTemplates"
        v-on-click-outside="closeFilterMenu"
        class="flex flex-wrap gap-2 items-center"
      >
        <div v-for="menu in filterMenus" :key="menu.key" class="relative">
          <Button
            :icon="menu.icon"
            color="slate"
            size="sm"
            :class="{ 'bg-n-slate-9/10': openFilterMenu === menu.key }"
            @click="toggleFilterMenu(menu.key)"
          >
            <span class="min-w-0 truncate">{{ menu.selected.label }}</span>
            <Icon icon="i-lucide-chevron-down" class="shrink-0 size-4" />
          </Button>
          <DropdownMenu
            v-if="openFilterMenu === menu.key"
            :menu-items="menu.items"
            class="mt-2 min-w-52 top-full ltr:left-0 rtl:right-0"
            @action="handleFilterAction"
          />
        </div>
        <Input
          v-model="searchQuery"
          :placeholder="t('WHATSAPP_TEMPLATE_MGMT.SEARCH_PLACEHOLDER')"
          class="max-w-xs"
        />
      </div>

      <div v-if="isLoading" class="flex justify-center items-center p-8">
        <SpinnerLoader :size="24" class="text-n-blue-9" />
      </div>

      <div
        v-else-if="!templates.length"
        class="flex justify-center items-center p-8 text-center text-n-slate-11"
      >
        {{ t('WHATSAPP_TEMPLATE_MGMT.EMPTY') }}
      </div>

      <div
        v-else-if="!filteredTemplates.length"
        class="flex justify-center items-center p-8 text-center text-n-slate-11"
      >
        {{ t('WHATSAPP_TEMPLATE_MGMT.NO_RESULTS') }}
      </div>

      <div v-else class="border-t divide-y divide-n-weak border-n-weak">
        <TemplateCard
          v-for="template in filteredTemplates"
          :key="template.key"
          :template="template"
          @preview="openPreview(template)"
        />
      </div>
    </div>

    <TemplatePreviewDrawer ref="previewPanelRef" :template="selectedTemplate" />
  </div>
</template>
